import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient, forgetApiKey } from '../api.js';
import { BookmarkletDragWidget } from '../components/BookmarkletDragWidget.jsx';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { MintKeyModal } from '../components/MintKeyModal.jsx';
import { DeleteIcon, KeyIcon } from '../components/icons';
import { formatDate, KindBadge, PurposeBadge } from './ApiKeys.jsx';

/**
 * Project-scoped bookmarklet page (card 063).
 *
 * Restores the project-scoped "bookmarklet for this project" surface
 * that card 053 inadvertently folded into the personal `/my-keys`
 * page.  Sits under the project Setup nav group alongside Install /
 * Sites / Auth / Webhooks.
 *
 * Behaviour:
 *
 *   - Loads this project's API keys filtered to `purpose=bookmarklet`
 *     (card 048's tag).  Lists each so the operator can see prior
 *     bookmarklets (mint date, expiry, who minted).
 *   - When none exist: surface a one-click "Mint bookmarklet for this
 *     project" CTA wired to the shared MintKeyModal in modal mode
 *     with the bookmarklet preset locked in and ownership=personal.
 *     The minted key is personal-scoped so leaving the tenant auto-
 *     revokes it — the same blast-radius semantics as `/my-keys`.
 *   - When one or more exist: the topmost row is auto-expanded with
 *     the drag widget so a returning user lands ready to grab the
 *     existing bookmarklet.
 *
 * The widget itself lives in `components/BookmarkletDragWidget.jsx`,
 * shared with the personal `/my-keys#bookmarklet` panel.
 */
export function ProjectBookmarklet() {
  const { getToken } = useAuth();
  const { project } = useOutletContext() || {};
  const projectId = project?.project_id;
  const api = useMemo(
    () => createApiClient(getToken, { projectId }),
    [getToken, projectId],
  );

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMint, setShowMint] = useState(false);
  const [confirmNode, ask] = useConfirm();

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await api(
        `/v1/admin/api-keys?project_id=${encodeURIComponent(projectId)}`,
      );
      const all = resp?.keys || [];
      // Bookmarklet-purpose pk_live_* keys only.  We surface active
      // rows; archived / revoked don't help the operator drag a
      // working bookmarklet.
      const bookmarkletKeys = all.filter((k) => {
        const purpose = k.effective_purpose || k.purpose;
        return purpose === 'bookmarklet' && k.active !== false;
      });
      // Newest first — the "current" bookmarklet is almost always the
      // most recent mint.
      bookmarkletKeys.sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      });
      setKeys(bookmarkletKeys);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api, projectId]);

  useEffect(() => { load(); }, [load]);

  function revokeOne(k) {
    ask({
      title: `Revoke ${k.prefix}…?`,
      body: (
        <>
          <p>
            Your bookmark-bar bookmarklet using this key will stop
            working until you replace it with one minted from a fresh
            key.
          </p>
          <p>
            Other people using this same key from their bookmark bar
            will also get 401 on their next click.
          </p>
        </>
      ),
      confirmLabel: 'Revoke',
      danger: true,
      runOnConfirm: async () => {
        await api(
          `/v1/admin/api-keys/${encodeURIComponent(k.prefix)}`,
          { method: 'DELETE' },
        );
        forgetApiKey(k.prefix);
        load();
      },
    });
  }

  return (
    <section className="stack" style={{ marginTop: 4 }}>
      <style>{PROJECT_BOOKMARKLET_CSS}</style>
      <div>
        <h2 style={{ margin: '0 0 4px' }}>Bookmarklet</h2>
        <p style={{ color: 'var(--text-3)', margin: 0, fontSize: 13.5 }}>
          Drag the link onto your bookmark bar.  Click it on any page
          and Spotlight boots in admin mode for{' '}
          <strong>{project?.name || 'this project'}</strong> — the
          overlay opens, ready to author.  Other projects each have
          their own bookmarklet; switching projects in the sidebar
          shows that project&apos;s version.
        </p>
        <p style={{ color: 'var(--text-3)', margin: '6px 0 0', fontSize: 12.5 }}>
          The pk_live_* key bundled into the bookmarklet URL is
          publishable on its own — admin elevation happens at runtime
          when the SDK confirms you&apos;re a tenant member via your
          JWT.  Safe to leave on a shared bookmark bar; leaving the
          tenant auto-revokes the key (personal ownership).
        </p>
      </div>

      {loading && (
        <div className="state"><div className="state__title">Loading…</div></div>
      )}
      {error && !loading && (
        <div className="state state--error">
          <div className="state__title">Couldn&apos;t load bookmarklet keys</div>
          <div>{error}</div>
        </div>
      )}

      {!loading && !error && keys.length === 0 && (
        <div className="card project-bookmarklet__empty">
          <div className="state__title">
            No bookmarklet for{' '}
            <strong>{project?.name || 'this project'}</strong> yet
          </div>
          <p style={{ color: 'var(--text-3)', margin: '6px 0 12px', fontSize: 13 }}>
            Mint a project-scoped pk_live_* key tagged{' '}
            <code>purpose=bookmarklet</code>.  The key bakes into the
            javascript: URL the widget hands you to drag.  Default
            30-day expiry — comes back here when you need to renew.
          </p>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setShowMint(true)}
          >
            <KeyIcon /> Mint bookmarklet for this project
          </button>
        </div>
      )}

      {!loading && !error && keys.length > 0 && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => setShowMint(true)}
              title="Mint another bookmarklet key for this project"
            >
              <KeyIcon /> Mint another
            </button>
          </div>
          <ul className="list">
            {keys.map((k, idx) => (
              <ProjectBookmarkletRow
                key={k.prefix}
                k={k}
                projectSlug={project?.slug || ''}
                projectName={project?.name || ''}
                defaultExpanded={idx === 0}
                onRevoke={() => revokeOne(k)}
              />
            ))}
          </ul>
          <p style={{ color: 'var(--text-3)', margin: 0, fontSize: 12.5 }}>
            Looking for a different shape of personal key (MCP, CLI,
            general server-side)?  Mint those from{' '}
            <Link to="/my-keys">My API keys</Link> — those are bound
            to your user without project scoping.
          </p>
        </>
      )}

      {showMint && (
        <MintKeyModal
          api={api}
          projectId={projectId}
          mode="modal"
          presetFilter="personal"
          forceOwnership="personal"
          defaultPurpose="bookmarklet"
          onCreated={() => { load(); }}
          onClose={() => setShowMint(false)}
        />
      )}

      {confirmNode}
    </section>
  );
}

function ProjectBookmarkletRow({
  k,
  projectSlug,
  projectName,
  defaultExpanded,
  onRevoke,
}) {
  return (
    <li className="list__row project-bookmarklet-row">
      <div className="list__primary">
        <span className="list__name">
          {k.name || <em style={{ color: 'var(--text-3)' }}>untitled</em>}
          <KindBadge kind={k.kind || 'pk'} />
          <PurposeBadge k={k} />
        </span>
        <span className="list__meta">
          <code>{k.prefix}…</code>
          {' · '}
          created {formatDate(k.created_at)}
          {k.expires_at && <> · expires {formatDate(k.expires_at)}</>}
          {k.last_used_at && <> · last clicked {formatDate(k.last_used_at)}</>}
        </span>
      </div>
      <div className="list__actions">
        <button
          type="button"
          className="btn btn--danger-ghost"
          onClick={onRevoke}
          title="Revoke this key"
        >
          <DeleteIcon /> Revoke
        </button>
      </div>
      <div className="project-bookmarklet-row__widget">
        <BookmarkletDragWidget
          apiKey={{ prefix: k.prefix, name: k.name }}
          projectSlug={projectSlug}
          projectName={projectName}
          defaultExpanded={defaultExpanded}
        />
      </div>
    </li>
  );
}

const PROJECT_BOOKMARKLET_CSS = `
  .project-bookmarklet__empty {
    text-align: left;
  }
  .project-bookmarklet-row {
    flex-wrap: wrap;
  }
  .project-bookmarklet-row__widget {
    flex-basis: 100%;
    margin-top: 10px;
  }
`;
